import { ThemeMode } from "@amzn/stencil-react-components/context";
import { trackStructEvent } from "@snowplow/browser-tracker";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { Day1EventAction, Day1EventCategory } from "@/types/snowplow-events";

interface ThemeModeContextType {
  themeMode: ThemeMode;
  toggleThemeMode: () => void;
}

const ThemeModeContext = createContext<ThemeModeContextType | undefined>(
  undefined
);

/**
 * Temporarily disables all CSS transitions during theme changes to prevent visual flashing.
 * @see https://stencil.a2z.com/foundations/design-tokens#how-to-prevent-flashing-when-switching-themes
 */
function temporarilyDisableTransitions(): void {
  const style = document.createElement("style");
  style.setAttribute("data-theme-transition-control", "true");
  style.textContent = "*, *::before, *::after { transition: none !important; }";
  document.head.appendChild(style);

  setTimeout(() => {
    requestAnimationFrame(() => {
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    });
  }, 300);
}

const THEME_STORAGE_KEY = "ezo-theme-mode";

const getInitialTheme = (): ThemeMode => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === ThemeMode.Light || stored === ThemeMode.Dark) return stored;
  } catch {
    // localStorage unavailable
  }
  return ThemeMode.Dark;
};

export const ThemeModeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [themeMode, setThemeMode] = useState<ThemeMode>(getInitialTheme);

  const toggleThemeMode = useCallback(() => {
    temporarilyDisableTransitions();
    const next =
      themeMode === ThemeMode.Light ? ThemeMode.Dark : ThemeMode.Light;
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // localStorage unavailable
    }
    trackStructEvent({
      category: Day1EventCategory.Header,
      action: Day1EventAction.ThemeToggle,
      property: next.toUpperCase(),
    });
    setThemeMode(next);
  }, [themeMode]);

  const value = useMemo(
    () => ({ themeMode, toggleThemeMode }),
    [themeMode, toggleThemeMode]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      {children}
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = (): ThemeModeContextType => {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error("useThemeMode must be used within a ThemeModeProvider");
  }
  return context;
};
